import { useSelector } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { getUserProfile } from "../configuration/login/loginSlice";

import { fetchMaintenance } from "../api/fetchMaintenance";
import { fetchComplementaryAssetsDetails, fetchUptimeAssets } from "../api/fetchAssets";
import { fetchCheckupByMetricAndModel } from "../api/fetchCheckup";
import { fetchConsultants } from "../api/fetchConsultants";
import { CheckupExistsAppointment, fetchCheckupExistsByChassis, fetchScheduling } from "../api/fetchScheduling";
import { fetchContractByVehicle } from "../api/fetchContract";

import { Asset } from "../models/asset";

import useAssetGeoLocation from "./useAssetGeoLocation";
import { addDays, getFirstDayOfWeek, getLastDayOfWeek, getStringWeekDaysStartingFromDate } from "../utils/dateUtils";
import { createAppointmentFormFromScheduling } from "../services/schedulingService";
import { addHours } from "date-fns";
import { IAppointmentForm } from "./useSchedulingForm";
import useAssets from "./useAssets";
import { extractContractModality, extractContractNumber } from "../utils/contractUtils";

export function useConsultantSchedulingForm(dealershipMockData: any[]) {
  const userProfile = useSelector(getUserProfile);
  
  const { allAssets } = useAssets();

  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [appointment, setAppointment] = useState<IAppointmentForm | undefined>({
    assetsAndDrivers: {
      driver: { id: "-", name: "-", license: "", phone: "-", email: "-" }
    },
    dealership: { id: "", name: "", address: "", geoLocation: { lat: 0, lng: 0 } },
    dateAndConsultant: {},
  });
  const [assets, setAssets] = useState<Asset[]>([]);
  const [searchParams] = useSearchParams();
  const [appointmentId, setAppointmentId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      setAssets(allAssets);

      const schedulingId = searchParams.get("scheduling-id");
      let appointmentToUpdate: any = undefined;
      if (schedulingId) {
        appointmentToUpdate = await fetchScheduling(schedulingId);
        if (appointmentToUpdate) setAppointmentId(schedulingId);
      }

      const dealership = dealershipMockData.find(d => d.consultants.includes(userProfile?.sub?.split(':')[1] || ''));
      setAppointment({
        ...(appointmentToUpdate ? createAppointmentFormFromScheduling(appointmentToUpdate, []) : appointment),
        dealership: {
          id: dealership?.id || '',
          name: dealership?.name || '',
          address: dealership?.address || '',
          geoLocation: { lat: 0, lng: 0 }
        },
        dateAndConsultant: {
          consultant: {
            id: userProfile?.sub?.split(':')[1] || '',
            name: userProfile?.name || ''
          }
        }
      })

      setLoading(false);
    }

    fetchData()
  }, []);

  return {
    step, setStep,
    loading,
    appointment, setAppointment, appointmentId,
    assets,
  }
}

export function useConsultantSchedulingFormAssetDriverAndDate(assets: Asset[], form: IAppointmentForm, setForm: (form: IAppointmentForm) => void) {
  const [searchParams] = useSearchParams();
  const [assetId, setAssetId] = useState<string>('');
  const [, setIdentification] = useState<string>('');
  const [firstWeekDay, setFirstWeekDay] = useState<Date>(
    getFirstDayOfWeek(new Date())
  );
  const [weekDays, setWeekDays] = useState<any[]>([]);
  const [consultantScheduleDates, setConsultantScheduleDates] = useState<
    Date[]
  >([]);
  const [choosedDate, setChoosedDate] = useState<Date | null>(null);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vehicleLoading, setVehicleLoading] = useState(false);
  const [vehicleError, setVehicleError] = useState<string>('');
  const [disableAssetGroup, setDisableAssetGroup] = useState(false);
  const [hasProcessedUrl, setHasProcessedUrl] = useState(false);
  const [showDuplicateSchedulingDialog, setShowDuplicateSchedulingDialog] = useState(false);
  const [activeAppointment, setActiveAppointment] = useState<CheckupExistsAppointment | null>(null);

  const { allAssets } = useAssets();

  const { getAssetGeoLocation } = useAssetGeoLocation();

  useEffect(() => {
    const fetchData = async () => {
      const assetId = searchParams.get("asset");
      const identification = searchParams.get("chassis") ?? allAssets.find(asset => asset.id === assetId)?.chassis;
      const name = searchParams.get("name") ?? allAssets.find(asset => asset.id === assetId)?.name;
      
      console.log('URL params:', { assetId, identification, name });
      console.log('allAssets length:', allAssets.length);
      
      if (assetId && identification && !hasProcessedUrl) {
        console.log('Executando chooseAsset...');
        setHasProcessedUrl(true);
        setIdentification(identification);
        const selected = await chooseAsset(assetId, identification, name ?? '');
        if (selected) {
          setAssetId(assetId);
        }
      } else {
        console.log('Condição não atendida:', { assetId, identification, hasProcessedUrl });
      }
    };

    const date = searchParams.get("date");
    if (date) {
      const localeDate = addHours(new Date(date), 3);
      setChoosedDate(localeDate);
      selectDate(localeDate);
      getConsultantAvailability(localeDate);
    }

    if (allAssets.length > 0) {
      fetchData();
    }
  }, [assets, searchParams, allAssets]);

  useEffect(() => {
    setWeekDays(
      getStringWeekDaysStartingFromDate(firstWeekDay).map((date, index) => ({
        date: addDays(firstWeekDay, index),
        weekDay: date.split(" ")[0],
        day: date.split(" ")[1],
      }))
    );
    if (form?.dateAndConsultant?.scheduleDate)
      setChoosedDate(form?.dateAndConsultant?.scheduleDate);
  }, [assets]);

  const previousWeek = () => {
    const lastDayOfPreviousWeek = addDays(firstWeekDay, -1);
    const firstWeekDayOfPreviousWeek = getFirstDayOfWeek(lastDayOfPreviousWeek);
    setWeekDays(
      getStringWeekDaysStartingFromDate(firstWeekDayOfPreviousWeek).map(
        (date, index) => ({
          date: addDays(firstWeekDayOfPreviousWeek, index),
          weekDay: date.split(" ")[0],
          day: date.split(" ")[1],
        })
      )
    );
    setFirstWeekDay(firstWeekDayOfPreviousWeek);
  };

  const nextWeek = () => {
    const lastWeekDay = getLastDayOfWeek(firstWeekDay);
    const firstDayOfNextWeek = addDays(lastWeekDay, +1);
    setWeekDays(
      getStringWeekDaysStartingFromDate(firstDayOfNextWeek).map(
        (date, index) => ({
          date: addDays(firstDayOfNextWeek, index),
          weekDay: date.split(" ")[0],
          day: date.split(" ")[1],
        })
      )
    );
    setFirstWeekDay(firstDayOfNextWeek);
  };

  const selectDate = (selectedDate: Date | undefined) => {
    setForm({
      ...form,
      dateAndConsultant: {
        ...form?.dateAndConsultant,
        scheduleDate: selectedDate,
      },
    });
  };

  const handleClose = () => {
    setShowDialog(false);
  };

  const closeDuplicateSchedulingDialog = () => {
    setShowDuplicateSchedulingDialog(false);
    setActiveAppointment(null);
    setAssetId("");
    setForm({
      ...form,
      assetsAndDrivers: {
        ...form?.assetsAndDrivers,
        asset: undefined,
      },
    });
  };

  const chooseAsset = async (assetId: string, identification: string, name: string): Promise<boolean> => {
    try {
      console.log('chooseAsset chamada com:', { assetId, identification, name });
      setVehicleLoading(true);
      setVehicleError('');
      
      const [assetsFetched] = await fetchMaintenance([{id: assetId, identification, name}]);
      console.log('assetsFetched:', assetsFetched);
      
      if (!assetsFetched.length) {
        setVehicleError('Veículo não encontrado');
        return false;
      }
      let asset = assetsFetched[0];

      const checkupExists = await fetchCheckupExistsByChassis(asset.chassis || identification);
      if (checkupExists.hasActive) {
        setActiveAppointment(checkupExists.appointment ?? null);
        setShowDuplicateSchedulingDialog(true);
        setVehicleLoading(false);
        return false;
      }

      const geoLocation = await getAssetGeoLocation(assetId);
      asset.geoLocation = geoLocation;

      if (!asset.odometer || !asset.hourmeter) {
        const uptimeAssets = await fetchUptimeAssets([asset.id]);
        const uptimeAsset = uptimeAssets[0];
        if (!asset.odometer && uptimeAsset?.odometer) {
          asset.odometer = uptimeAsset?.odometer;
          asset.lastOdometerUpdate = uptimeAsset?.lastOdometerUpdate;
        }

        if (!asset.hourmeter && uptimeAsset?.hourmeter) {
          asset.hourmeter = uptimeAsset?.hourmeter;
          asset.lastHourmeterUpdate = uptimeAsset?.lastHourmeterUpdate;
        }
      }

      const complementaryAssetsDetails = await fetchComplementaryAssetsDetails([asset.id]);
      const complementaryAsset = complementaryAssetsDetails[0];

      if (complementaryAsset?.volkstotal) {
        (asset as any).volkstotal = complementaryAsset.volkstotal;
      }

      const contractModalityFromSummary =
        extractContractModality(asset) ??
        extractContractModality(complementaryAsset ?? {});

      let contractNumber =
        extractContractNumber(asset) ??
        extractContractNumber(complementaryAsset ?? {}) ??
        null;

      const assetVolksTotal = (asset as any).volkstotal ?? (asset as any).Volkstotal ?? (asset as any).VolksTotal;

      if (!contractNumber && assetVolksTotal?.contractNumber) {
        contractNumber = assetVolksTotal.contractNumber;
      }
      if (!contractNumber) {
        const hasValidPlan = asset.plans?.some(plan => 
          typeof plan.name === 'string' && plan.name.toLowerCase().includes('volkstotal')
        );
        
        if (hasValidPlan) {
          contractNumber = await fetchContractByVehicle(asset.chassis);
        }
      }

      if (contractNumber) {
        asset.contractNumber = contractNumber;
      }

      if (contractModalityFromSummary) {
        asset.contractModality = contractModalityFromSummary;
      }

      setForm({
        ...form,
        assetsAndDrivers: {
          ...form?.assetsAndDrivers,
          asset,
        },
      });
      setDisableAssetGroup(!!asset.group);
      chooseCheckup(asset);
      setAssetId(assetId);
      return true;
    } catch (error: any) {
      console.error('Erro ao buscar informações do veículo:', error);
      setVehicleError('Erro ao carregar informações do veículo. Tente novamente.');
      return false;
    } finally {
      setVehicleLoading(false);
    }
  };

  const chooseCheckup = async (asset: Asset | undefined) => {
    const checkup = asset
      ? await fetchCheckupByMetricAndModel(
        asset?.odometer ?? "",
        asset?.hourmeter ?? "",
        asset?.chassis ?? "",
        asset?.group ?? "",
        asset?.modelCode ?? ""
      )
      : undefined;
    setForm({
      ...form,
      assetsAndDrivers: {
        ...form?.assetsAndDrivers,
        checkup,
        asset,
      },
    });
  };

  const getCheckupByMetric = async (
    odometer: number,
    hourmeter: number,
    chassis: string,
    group: string,
    model?: string
  ) => {
    return await fetchCheckupByMetricAndModel(
      odometer,
      hourmeter,
      chassis,
      group,
      model
    );
  };

  const formIsValid = (): boolean => {
    return !!(
      form?.assetsAndDrivers?.asset &&
      form?.assetsAndDrivers?.checkup &&
      form?.schedulingTypes &&
      form?.dateAndConsultant?.scheduleDate
    );
  };

  const getConsultantAvailability = async (date: Date) => {
    if (form?.dealership?.id) {
      const consultants = await fetchConsultants(date, form.dealership.id);
      const consultant = consultants.find(
        (el) => el.id === form?.dateAndConsultant?.consultant?.id
      );
      if (consultant && consultant.scheduleDates)
        setConsultantScheduleDates(consultant.scheduleDates);
    }
  };

  const setCheckup = useMemo(
    () => debounce(async (metricValue: number, metricType: string, asset?: Asset) => {
      setLoading(true);
      const checkup = await getCheckupByMetric(
        metricType === 'odometer' ? metricValue : asset?.odometer ?? 0,
        metricType === 'hourmeter' ? metricValue : asset?.hourmeter ?? 0,
        asset?.chassis ?? "",
        asset?.group ?? "",
        asset?.modelCode ?? ""
      );
      setForm({
        ...form,
        assetsAndDrivers: {
          ...form?.assetsAndDrivers,
          checkup,
          asset: asset ? {
          ...asset,
          [metricType]: metricValue,
        } : undefined,
        },
      });
      setLoading(false);
    }, 500), [],
  );

  return {
    assetId,
    firstWeekDay,
    weekDays,
    consultantScheduleDates,
    choosedDate, setChoosedDate,
    showDialog, setShowDialog,
    loading, setLoading,
    vehicleLoading, setVehicleLoading,
    vehicleError, setVehicleError,
    disableAssetGroup,
    showDuplicateSchedulingDialog, activeAppointment, closeDuplicateSchedulingDialog,
    previousWeek, nextWeek, selectDate,
    handleClose, chooseAsset, getCheckupByMetric, setCheckup,
    formIsValid,
    getConsultantAvailability,
  }

}

export function useConsultantSchedulingFormSummary() {
  const userProfile = useSelector(getUserProfile);
  const [loading, setLoading] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const [errorDialog, setErrorDialog] = useState({
    show: false,
    title: "",
    body: "",
  });

  return {
    userProfile,
    loading, setLoading,
    successDialogOpen, setSuccessDialogOpen,
    errorDialog, setErrorDialog,
  }
}